/**
 * The host the app runs inside.
 *
 * Telegram gives us a signed identity and a theme. A plain browser tab gives us
 * neither, so the web platform fakes both. Nothing else in the app knows which
 * one it got; that is what will let xchange run on its own domain later with a
 * different login.
 */
export interface Platform {
  readonly kind: 'telegram' | 'web';
  /** Credential to send to the API. Telegram initData, or a mock user for web. */
  credential():
    | { readonly kind: 'telegram'; readonly initData: string }
    | {
        readonly kind: 'mock';
        readonly userId: string;
        readonly displayName: string;
        readonly botId?: string;
      }
    /** A plain browser: the wallet on this device signs in, with a PIN. */
    | { readonly kind: 'wallet' };
  /** Tell the host we are ready to be shown. */
  ready(): void;
  /** Open a link in the host's browser. */
  openLink(url: string): void;
  /** Light haptic tick on success, where the host supports it. */
  tick(): void;
  /** Close the app, where the host supports it. */
  close(): void;
  /** Share xchange with someone: the host's share sheet, or a copied link. */
  share(text: string, url: string): Promise<'shared' | 'copied' | 'unavailable'>;
  /**
   * Durable storage tied to the person, not the device: Telegram's
   * CloudStorage, which follows their account across phones and reinstalls.
   * The web platform uses localStorage, which follows nothing.
   */
  storage: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    remove(key: string): Promise<void>;
  };
}

export async function detectPlatform(): Promise<Platform> {
  const forced = import.meta.env.VITE_PLATFORM as string | undefined;
  const hasTelegram =
    typeof window !== 'undefined' &&
    'Telegram' in window &&
    Boolean(
      (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData,
    );
  if (forced === 'web' || (!hasTelegram && forced !== 'telegram')) {
    const { webPlatform } = await import('./web.js');
    return webPlatform();
  }
  const { telegramPlatform } = await import('./telegram.js');
  return telegramPlatform();
}
